import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";

const SocketContext = createContext(null);

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:5000";

export function SocketProvider({ children }) {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const listenersRef = useRef(new Map());

  const connect = useCallback((token) => {
    if (socketRef.current?.connected) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    socket.on("connect", () => {
      setIsConnected(true);
      socket.emit("get_online_users");
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
    });

    socket.on("online_users", (data) => {
      setOnlineUsers(data.users || []);
    });

    socket.on("user_connected", (data) => {
      setOnlineUsers((prev) => {
        if (prev.find((u) => u.user_id === data.user_id)) return prev;
        return [...prev, { ...data, status: "idle" }];
      });
    });

    socket.on("user_disconnected", (data) => {
      setOnlineUsers((prev) => prev.filter((u) => u.user_id !== data.user_id));
    });

    socket.on("presence_updated", (data) => {
      setOnlineUsers((prev) =>
        prev.map((u) =>
          u.user_id === data.user_id
            ? { ...u, status: data.status, campaign_id: data.campaign_id }
            : u
        )
      );
    });

    socketRef.current = socket;
  }, []);

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    setIsConnected(false);
    setOnlineUsers([]);
  }, []);

  const joinRoom = useCallback((room) => {
    socketRef.current?.emit("join", { room });
  }, []);

  const leaveRoom = useCallback((room) => {
    socketRef.current?.emit("leave", { room });
  }, []);

  const joinCampaign = useCallback((campaignId) => {
    socketRef.current?.emit("join_campaign", { campaign_id: campaignId });
  }, []);

  const leaveCampaign = useCallback((campaignId) => {
    socketRef.current?.emit("leave_campaign", { campaign_id: campaignId });
  }, []);

  const updatePresence = useCallback((data) => {
    socketRef.current?.emit("presence_update", data);
  }, []);

  const sendTypingIndicator = useCallback((campaignId, contactId, isTyping) => {
    socketRef.current?.emit("typing_indicator", {
      campaign_id: campaignId,
      contact_id: contactId,
      is_typing: isTyping,
    });
  }, []);

  const on = useCallback((event, handler) => {
    socketRef.current?.on(event, handler);
    // Track for cleanup
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(handler);
  }, []);

  const off = useCallback((event, handler) => {
    socketRef.current?.off(event, handler);
    listenersRef.current.get(event)?.delete(handler);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  const value = {
    socket: socketRef.current,
    isConnected,
    onlineUsers,
    connect,
    disconnect,
    joinRoom,
    leaveRoom,
    joinCampaign,
    leaveCampaign,
    updatePresence,
    sendTypingIndicator,
    on,
    off,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error("useSocket must be used within SocketProvider");
  return ctx;
}
